import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { fetchHelpSectionArticles } from "@/api/help";
import { HelpArticleCard } from "@/components/help/HelpArticleCard";
import { useDelayedFlag } from "@/components/help/useDelayedFlag";
import { GfgButton } from "@/components/ui/GfgButton";
import { HELP_TOPIC_LABELS } from "@/constants/help";
import type { HelpSection } from "@/interfaces/help";
import { apiErrorMessage } from "@/utils/apiError-helper";

const SKELETON_KEYS = ["skeleton-1", "skeleton-2", "skeleton-3"];

export interface HelpSectionViewProps {
  section: HelpSection;
  /** Focus this view's heading on show — set when it was reached from inside the panel. */
  focusOnShow: boolean;
  onSelectArticle: (slug: string) => void;
}

/** The panel rail's Topics listing: every published article in one helpArticle.section. */
export function HelpSectionView({ section, focusOnShow, onSelectArticle }: HelpSectionViewProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const {
    data: articles,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["help", "section", section],
    queryFn: () => fetchHelpSectionArticles(section),
  });

  const showSkeleton = useDelayedFlag(isLoading);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `section` is the trigger — a new topic takes focus.
  useEffect(() => {
    if (focusOnShow) headingRef.current?.focus();
  }, [focusOnShow, section]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `section` is an intentional trigger the body doesn't read — a new topic must start at the top of the pane.
  useEffect(() => {
    rootRef.current?.closest<HTMLElement>("[data-help-pane]")?.scrollTo({ top: 0 });
  }, [section]);

  return (
    <div ref={rootRef} className="mx-auto w-full max-w-[560px] max-[900px]:max-w-none">
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="m-0 font-crimson font-normal text-[24px] text-rodeo-black outline-none"
      >
        {HELP_TOPIC_LABELS[section]}
      </h2>
      {!isLoading && !isError && !!articles && (
        <p className="mt-[5px] max-w-[60ch] text-[13px] text-graphite">
          {articles.length} article{articles.length === 1 ? "" : "s"}
        </p>
      )}

      <div className="mt-5 flex flex-col">
        {showSkeleton && (
          <div className="flex flex-col gap-3">
            {SKELETON_KEYS.map((key) => (
              <div
                key={key}
                className="h-16 animate-pulse rounded-card bg-wax"
                aria-hidden="true"
              />
            ))}
          </div>
        )}

        {isError && (
          <div className="flex flex-col items-center gap-3 rounded-card border border-card-border py-10 text-center">
            <p className="text-[14px] text-graphite">
              {apiErrorMessage(error, "We couldn't load this topic's articles. Please try again.")}
            </p>
            <GfgButton type="button" variant="outline" onClick={() => refetch()}>
              Retry
            </GfgButton>
          </div>
        )}

        {!isLoading && !isError && articles?.length === 0 && (
          <div className="rounded-card border border-card-border py-10 text-center">
            <p className="text-[14px] text-graphite">No articles in this topic yet.</p>
          </div>
        )}

        {!isLoading &&
          !isError &&
          !!articles?.length &&
          articles.map((article) => (
            <HelpArticleCard key={article.slug} article={article} onSelect={onSelectArticle} />
          ))}
      </div>
    </div>
  );
}

export default HelpSectionView;
